import { patchesFor } from './drag-math';

import type { GanttItem, GanttPatch, Span } from '../types';

/**
 * The one settle sequence, shared by every commit path regardless of how its patches were
 * computed: skip `onCommit` ENTIRELY when there is nothing to send (not "call it with `[]`"),
 * otherwise call it, and clear the preview/state on both a resolved AND a rejected commit either
 * way.
 *
 * Extracted from `use-bar-drag.ts`'s `finish()` (Task 17's review flagged this as required before
 * Task 19's keyboard path could be built): if a second input method reimplemented this sequence by
 * hand and dropped the reject branch, the two methods would diverge on commit semantics while
 * agreeing perfectly on the day math — and no `drag-math.ts` test would ever catch that, because
 * the math is identical in both. `commitSpan` below is the `Span`-diffing entry point (drag,
 * keyboard); Task 20 split this tail out of it so `EmptyRowAdd` — which has no prior `Span` to
 * diff against on an undated row — can share the exact same skip/invoke/settle sequence with an
 * already-complete patch array, instead of either reimplementing the tail by hand (the divergence
 * this function exists to prevent) or feeding `patchesFor` a synthetic "previous span" it would
 * have to trust an untested arithmetic quirk to force through correctly.
 */
export const commitPatches = (args: {
  patches: GanttPatch[];
  onCommit?: (patches: GanttPatch[]) => Promise<void> | void;
  /** Called exactly once: synchronously when there's nothing to commit or `onCommit` returned
   * something other than a thenable, otherwise once the returned promise settles (either way). */
  onSettle: () => void;
}): void => {
  const { patches, onCommit, onSettle } = args;

  if (patches.length === 0) {
    onSettle();
    return;
  }

  const result = onCommit?.(patches);
  if (result && typeof (result as Promise<void>).then === 'function') {
    (result as Promise<void>).then(onSettle, onSettle);
  } else {
    onSettle();
  }
};

/**
 * The `Span`-diffing entry point, shared by every input method that can produce a `Span` preview
 * against a REAL prior span (pointer drag, keyboard arrows): compute the patches via `patchesFor`,
 * then hand them to `commitPatches` for the skip/invoke/settle sequence. Signature and behaviour
 * are unchanged from before Task 20's split — `use-bar-drag.ts` and `use-bar-keyboard.ts` call this
 * exactly as they did.
 */
export const commitSpan = (args: {
  item: GanttItem;
  span: Span;
  preview: Span;
  onCommit?: (patches: GanttPatch[]) => Promise<void> | void;
  /** Called exactly once: synchronously when there's nothing to commit or `onCommit` returned
   * something other than a thenable, otherwise once the returned promise settles (either way). */
  onSettle: () => void;
}): void => {
  const { item, span, preview, onCommit, onSettle } = args;

  commitPatches({ patches: patchesFor(item, span, preview), onCommit, onSettle });
};
